/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Button, Table } from "core/components";
import { MaterialRequestStatus, ProjectFormData } from "core/schema";
import moment from "moment";
import { useFieldArray } from "react-hook-form";

type ProjectCostBudgetProps = {
  project: ProjectFormData;
};

const ProjectMaterialRequest = ({
  materialRequest,
  control,
  reset,
  getValues,
  setValue,
  setItemsForDestruction
}: ProjectCostBudgetProps): JSX.Element => {
  const fieldArrayName = "material_request_items";
  const { fields, remove } = useFieldArray({
    control,
    name: fieldArrayName,
    keyName: "key"
  });
  const [materialRequestItems, setMaterialRequestItems] = React.useState(materialRequest.material_request_items);
  const [itemCount, setItemCount] = React.useState(materialRequest.material_request_items.length);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        Header: "Inventory ID",
        accessor: "item.code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Inventory Item",
        accessor: "item.name",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        Cell: (props) => {
          return <p className="text-center text-bold">{props.value || "-"}</p>;
        }
      },
      {
        Header: "Date Created",
        accessor: "created_at",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          );
        }
      },
      {
        Header: "Remarks",
        accessor: "remarks",
        Cell: (props) => {
          return (
            <p className="text-truncate" title={props.value} data-bs-toggle="tooltip">
              {props.value}
            </p>
          );
        }
      },
      {
        accessor: "actions",
        style: {
          width: "5%"
        },
        Cell: (props) => {
          const index = props.row.index;
          const id = props.row.original.id;

          if ([MaterialRequestStatus.Open, MaterialRequestStatus.ForApproval].includes(materialRequest.status) && getValues("item_count") > 1) {
            return (
              <Button
                className="button--danger"
                icon="minus"
                iconProps={{ className: "svg-icon h5" }}
                onClick={() => {
                  if (id) {
                    const item = materialRequestItems.find((item) => item?.id == id);
                    if (item) setItemsForDestruction((value) => [...value, { ...item, _destroy: "1" }]);

                    setMaterialRequestItems((items) => items.filter((item) => item.id != id));
                  }
                  setItemCount((prev) => prev - 1);
                  remove(index);
                }}
              />
            );
          } else {
            return <></>;
          }
        }
      }
    ],
    []
  );

  React.useEffect(() => {
    if (itemCount == materialRequest.material_request_items.length) {
      setItemsForDestruction([]);
    }
    reset({
      id: materialRequest.id,
      material_request_items: materialRequestItems
    });
    setValue("item_count", itemCount);
  }, [itemCount]);

  return (
    <React.StrictMode>
      <Table columns={columns} data={fields} />
    </React.StrictMode>
  );
};

export default ProjectMaterialRequest;
